import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Container } from 'react-bootstrap';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { addCommodityDescription, manageComcodes, validateLoad } from '../../../api/v2/load';
import { postVATRestrictions } from '../../../api/v2/businessPartner';
import ConfirmationModal from '../../ConfirmationModal';
import BulkOperations from '../../EpoListLoads/Details/Tabs/BulkOperations';
import { GoogleAnalyticsContext } from '../../GoogleAnalytics';
import { showCheckIpaffsButton } from '../Modals/IpaffsModal/utils';
import { useSecurity } from '../../Security/security';
import Spreadsheet from '../../Spreadsheet/Spreadsheet';
import RowNumberColumn from '../../Spreadsheet/RowNumberColumn/RowNumberColumn';
import { cleanseChedRef } from '../../../utils/stringValidation';
import { StoreContext } from '../../Store';
import Toast from '../../Toast/Toast';
import AuthModal from '../AuthModal';
import MultipleConsignorsModal from '../MultipleConsignorsModal/MultipleConsignorsModal';
import { ConfirmErrorsModal, ManageCommodityCodesModal } from '../Modals';
import { convertToGbp, removeEmptyOrNull } from '../QAChecklistTab/utils';
import GridTotalsPI from './GridTotalsPI';
import '../../../styles/TableEditor.css';
import ExciseModal from '../../ExciseModal/ExciseModal';
import { CODES_WITH_EXCISE } from '../../ExciseModal/constants';
import {
  CommodityCodeInfoTooltip,
  GrossMassInfoTooltip,
  NetMassInfoTooltip,
  PackageTypeInfoTooltip,
  PreferentialModal,
  PreferentialTooltip,
} from './Tooltips';
import { useDropdownOptions } from '../queryHooks/useDropdownOptions';
import { isLoadStatusSubmitted } from '../../../utils/unifiedLoads';
import { errorMessages, processDataForSpreadsheet, hasRowsWithoutInvoiceNumbers } from './utils';
import { useSupplementaryUnits } from './useSupplementaryUnits';
import { useMultipleConsignors } from './useMultipleConsignors';
import { useSpreadsheetTotals } from './useSpreadsheetTotals';

const ProductInfo = ({
  visible,
  nextPage,
  submit,
  resetTrigger,
  setResetDone,
  showQATab,
  hasErrors,
  spreadsheetHandle,
  eidrData,
  setShowIpaffsModal,
  getColumnIndex,
}) => {
  const store = useContext(StoreContext);
  const { validateRole } = useSecurity();
  const ga = useContext(GoogleAnalyticsContext);

  const { staticDropdowns } = useDropdownOptions();
  const { vatOptions, preferenceOptions, exciseOptions } = staticDropdowns.data || {};
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [hasValidatedRows, setHasValidatedRows] = useState(false);
  const [showMissingDescriptionModal, setShowMissingDescriptionModal] = useState(false);
  const [isLoadingDescriptions, setIsLoadingDescriptions] = useState(false);

  const [showPreferentialConfirmation, setShowPreferentialConfirmation] = useState(false);
  const [productsForReviewInModal, setProductsForReviewInModal] = useState([]);

  const isFromFile = store.newLoadFromFile[0];
  const fileData = store.newLoadFileData[0];
  const setNewLoadDataModified = store.newLoadDataModified[1];
  const setNewLoadItemsModified = store.newLoadItemsModified[1];

  const referenceNoDisplay = store.portalRef[0] ? ` - ${store.portalRef[0]}` : '';

  const [consignorDetails, setConsignorDetails] = store.multipleConsignorDetails;
  const isMultipleInvoices = store.multipleInvoices[0];
  const isMultipleConsignors = store.multipleConsignors[0];

  const [isExciseModalOpen, setIsExciseModalOpen] = useState(false);
  const isLoadSubmitted = isLoadStatusSubmitted(store.loadStatus[0]);

  const [isMultipleInvoicesValid, setIsMultipleInvoicesValid] = store.isMultipleInvoicesValid;
  const [isMultipleConsignorsValid, setIsMultipleConsignorsValid] = store.isMultipleConsignorsValid;

  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showManageCommodityCodesModal, setShowManageCommodityCodesModal] = useState(false);
  const [showConfirmErrorsModal, setShowConfirmErrorsModal] = useState(false);

  /** Tanstack Query Client */
  const queryClient = useQueryClient();

  const [alertDetails, setAlertDetails] = useState({ title: '', description: '' });
  const [showAlert, setShowAlert] = useState(false);

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [vatAuthEntered, setVatAuthEntered] = useState(false);
  const [previousWarnings, setPreviousWarnings] = useState('');

  const handleTableClicked = useCallback(() => {
    if (showAlert) {
      setShowAlert(false);
    }
  }, [showAlert]);

  const addDescriptionMutation = useMutation({
    mutationFn: (payload) => addCommodityDescription({ items: payload }),
  });

  const manageComcodesMutation = useMutation({
    mutationFn: (payload) => manageComcodes(payload),
  });

  /** Process data from database or from RPA file so it's compatible with Spreadsheet component */
  useEffect(() => {
    if (vatOptions && preferenceOptions) {
      const items = isFromFile && fileData?.Items ? fileData.Items : eidrData?.Items;

      if (items?.length) {
        const rows = processDataForSpreadsheet(
          items,
          isMultipleInvoices,
          isMultipleConsignors,
          vatOptions,
          preferenceOptions
        );
        spreadsheetHandle.current.excelDispatch({ type: 'set', detail: rows });

        if (isMultipleConsignors) {
          fetchConsignorDetails(items);
        }
      }
    }
    setNewLoadDataModified(false);
  }, [eidrData, isFromFile, fileData, vatOptions, preferenceOptions, spreadsheetHandle.current.excelDispatch]);

  /** When hasValidated rows is set to true, we submit the form to Save as Draft */
  useEffect(() => {
    if (hasValidatedRows && typeof submit === 'function') {
      submit(new Event('submit'));
      resetPage();
    }
  }, [hasValidatedRows]);

  const invoiceNumberIndex = getColumnIndex('invoiceNumber');
  const consignorIndex = getColumnIndex('neeNoridconsignor');

  const {
    invoiceConsignorMapping,
    invoiceLines,
    isMultipleConsignorsModalOpen,
    setIsMultipleConsignorsModalOpen,
    fetchConsignorDetails,
    handleUpdateConsignors,
    calculateIfMultipleConsignorsValid,
  } = useMultipleConsignors({
    spreadsheetHandle,
    isMultipleConsignors,
    invoiceNumberIndex,
    consignorDetails,
    setConsignorDetails,
    setIsMultipleConsignorsValid,
    setIsMultipleInvoicesValid,
  });

  const { netMass, grossMass, itemsCount, totalValue, itemRows, itemHeaders, showBulkBtn, resetTotals } =
    useSpreadsheetTotals({
      spreadsheetHandle,
      isMultipleInvoices,
      consignorDetails,
      calculateIfMultipleConsignorsValid,
      setTotalItemValue: store.totalItemValue[1],
    });

  /**
   * @param {boolean} [skipUpdate] - If true, do not collapse rows in the spreadsheet UI.
   * @param {boolean} [suppressNoProductsWarning] - If true, no toast when there are zero product lines (e.g. supplementary-units prefetch while user is typing).
   */
  const prepareProductList = (skipUpdate = false, suppressNoProductsWarning = false) => {
    const [collapsedRows, collapsedFiles] = getCollapsedRows(spreadsheetHandle);

    if (collapsedRows.length === 0) {
      if (!suppressNoProductsWarning) {
        setAlertDetails(errorMessages.noProductsEntered);
        setShowAlert(true);
      }
      return;
    }

    if (!skipUpdate) {
      spreadsheetHandle.current.excelDispatch({
        type: 'set',
        detail: collapsedRows,
      }); // update the visual rows to the newly collapsed - needed so error index = shown index.
      spreadsheetHandle.current.excelDispatch({
        type: 'set-files',
        detail: collapsedFiles,
      });
    }

    const items = collapsedRows.map((row, row_idx) => {
      const obj = {};
      spreadsheetHandle.current.excelState.config.forEach((col, idx) => {
        const { key } = col;
        obj[key] = row[idx]?.text ?? '';
        if (row[idx]?.id !== undefined) {
          obj['id'] = row[idx].id;
        }
      });

      // -- TODO: if dummy value is provided by backend, remove the below.
      if (!obj['commoditycode'] && store.tadEns[0]) {
        obj['commoditycode'] = null;
      }
      obj.file_ids = (collapsedFiles[row_idx] ?? []).map((file) => file.id);
      return obj;
    });

    const existingItemsById = new Map(store.validatedRowsReadyToSubmit[0].map((item) => [item.id, item]));

    // Backend doesn't like empty strings or nulls, preferred straight 'undefined'.
    const itemsWithoutMovement = removeEmptyOrNull(items).filter((value) => Object.keys(value).length !== 0);

    const filteredList = itemsWithoutMovement.map((x) => ({
      ...x,
      customsValue: existingItemsById.get(x.id)?.customsValue,
      movementIdSelected: store.directionSelected[0],
      despatchIdSelected: store.despatch[0],
      loadValue: convertToGbp(x.itemValue, store.exchangeRate[0]),
    }));

    return filteredList;
  };

  useSupplementaryUnits({
    visible,
    spreadsheetHandle,
    prepareProductList,
    GBimport: store.GBimport?.[0],
    GBexport: store.GBexport?.[0],
  });

  // Collapses any completely empty rows and squeezes non-empty rows together.
  const getCollapsedRows = (spreadsheetHandle) => {
    const collapsedFiles = [];
    let collapsedRows = [];

    for (const idx in spreadsheetHandle.current.excelState.rows) {
      const row = spreadsheetHandle.current.excelState.rows[idx];
      // check if row has any non-empty cells (excluding consignor column, which is hidden)
      if (row.some((cell, cellIndex) => cell?.text !== '' && cellIndex !== consignorIndex)) {
        collapsedRows.push(row);
        collapsedFiles.push(spreadsheetHandle.current.excelState.files[idx]);
      }
    }

    // Sets default values for any columns that require it.
    collapsedRows = collapsedRows.map((row) => {
      const new_row = [];
      spreadsheetHandle.current.excelState.config.forEach((col, idx) => {
        const { key, default_text } = col;
        let textValue = row[idx]?.text || default_text;

        // if consignor is empty, get the consignor ID for the row from the invoiceConsignorMapping object
        if (isMultipleConsignors && key === 'neeNoridconsignor') {
          const invoiceNumber = row[invoiceNumberIndex]?.text;
          textValue = invoiceConsignorMapping[invoiceNumber];
        }

        // Clear the exciseProductType and exciseTaxCode fields if the commodity code is not in the list of codes that require excise.
        if (key === 'exciseProductType' || key === 'exciseTaxCode') {
          const commCodeIndex = getColumnIndex('commoditycode');
          const commCode = row[commCodeIndex]?.text;
          const isValidForExcise = CODES_WITH_EXCISE.includes(commCode);
          if (!isValidForExcise) {
            textValue = '';
          }
        }

        new_row.push({
          ...row[idx],
          text: textValue ?? '',
        });
      });
      return new_row;
    });

    return [collapsedRows, collapsedFiles];
  };

  // get error, warning or autoCorrection for a field within responses from the backend
  // note - some fields don't have a column, i.e. neeNoridconsignor, so we need to map them to another column name
  const getErrorOrWarningFromResponses = (responses, field, rowIndex, columnIndex) => {
    return responses.find((response) => {
      const columnName = response.field === 'neeNoridconsignor' ? 'invoiceNumber' : response.field;
      return response.itemId - 1 === rowIndex && getColumnIndex(columnName) === columnIndex;
    });
  };

  const handleErrorsAndWarnings = (data) => {
    const { errors = [], warnings = [], autoCorrections = [] } = data;

    const updatedRows = spreadsheetHandle?.current?.excelState?.rows.map((row, rowIndex) => {
      const updatedRow = row.map((cell, columnIndex) => {
        const error = getErrorOrWarningFromResponses(errors, cell.field, rowIndex, columnIndex);
        const warning = getErrorOrWarningFromResponses(warnings, cell.field, rowIndex, columnIndex);
        const autoCorrection = getErrorOrWarningFromResponses(autoCorrections, cell.field, rowIndex, columnIndex);

        return {
          ...cell,
          error: error?.message ?? '',
          warn: warning?.message ?? '',
          auto: !!autoCorrection,
          text: autoCorrection ? autoCorrection.newValue : cell.text,
          autoMessage: autoCorrection ? autoCorrection.message : undefined,
        };
      });

      return updatedRow;
    });

    spreadsheetHandle.current.excelDispatch({
      type: 'set',
      detail: updatedRows,
    });
  };

  useEffect(() => {
    if (resetTrigger) {
      resetPage();
      setResetDone();
    }
  }, [resetTrigger, setResetDone]);

  const resetPage = () => {
    spreadsheetHandle.current.excelDispatch({ type: 'reset' });
    setShowConfirmation(false);
    store.resetNewLoad();
    store.isEidr[1](0);
    setHasValidatedRows(false);
  };

  /** Show preferential information modal if necessary - otherwise go to next page */
  const gotoNextPage = () => {
    setShowAlert(false);
    const rows = spreadsheetHandle?.current?.excelState?.rows;
    const prefColumnIndex = getColumnIndex('preferential');

    if (!showQATab && rows.some((row) => row[prefColumnIndex]?.text === 'YES')) {
      setShowPreferentialConfirmation(true);
    } else {
      const filteredList = prepareProductList(true);
      store.validatedRowsReadyToSubmit[1](filteredList);
      nextPage();
    }
  };

  useEffect(() => {
    const resetSpreadsheet = () => {
      store.validatedRowsReadyToSubmit[1]([]);
      setNewLoadItemsModified(false);
      setHasValidatedRows(false);
      resetTotals();
    };

    spreadsheetHandle.current.excelDispatch({
      type: 'setOnReset',
      detail: resetSpreadsheet,
    });
  }, []);

  useEffect(() => {
    if (visible) {
      spreadsheetHandle.current.excelDispatch({ type: 'trigger-update' });
    }
  }, [visible]);

  const hsCodeMinAllowedLength = 5;
  const hsCodeMaxAllowedLength = 10;
  const productInformationItems = spreadsheetHandle?.current?.excelState?.rows;

  const populateListOfInvalidProducts = (products) => {
    const invalidProducts = [];
    const commodityCodeSet = new Set();

    setProductsForReviewInModal([]);

    const commodityCodeIndex = getColumnIndex('commoditycode');
    const descriptionIndex = getColumnIndex('description');

    products?.forEach((product, index) => {
      const productCodeLength = product[commodityCodeIndex]?.text?.length;
      const productDescriptionLength = product[descriptionIndex]?.text?.length;
      const isInvalidLength = productCodeLength > hsCodeMinAllowedLength && productCodeLength < hsCodeMaxAllowedLength;

      if (isInvalidLength && productDescriptionLength > 0) {
        //Now that we have a product with invalid HS code, first we need to check if the invalid product are unique - we use HSCode and Description to check for uniqueness
        const commodityHSCodeDescription = `${product[commodityCodeIndex]?.text}-${product[descriptionIndex]?.text}`;

        if (!commodityCodeSet.has(commodityHSCodeDescription)) {
          commodityCodeSet.add(commodityHSCodeDescription);

          const tempProd = {
            id: index,
            commodityCode: product[commodityCodeIndex]?.text,
            description: product[descriptionIndex]?.text,
          };
          invalidProducts.push(tempProd);
        }
      }
    });

    if (invalidProducts.length > 0) {
      setProductsForReviewInModal(invalidProducts);
      checkForExistingCommodityCodeMappings(invalidProducts);
    }
  };

  const checkForExistingCommodityCodeMappings = async (productList) => {
    setProductsForReviewInModal([]);

    const commodityCodeIndex = getColumnIndex('commoditycode');
    const descriptionIndex = getColumnIndex('description');

    try {
      const payload = {
        data: productList,
        companyId: parseInt(store.clientID[0]),
      };

      const response = await manageComcodesMutation.mutateAsync(payload);

      if (response) {
        const productsForReview = [];

        response.forEach((product) => {
          //check if we have a product match based on HSCode and Description
          const productMatched =
            product?.hsCode?.trim() === productInformationItems[product.id][commodityCodeIndex]?.text?.trim() &&
            product?.description?.trim() === productInformationItems[product.id][descriptionIndex]?.text?.trim();

          if (product.valid && productMatched) {
            //when we have a product match, we update the HSCode for all matching products in the list
            productInformationItems?.forEach((item) => {
              if (
                item[commodityCodeIndex]?.text.trim() === product.hsCode.trim() &&
                item[descriptionIndex]?.text.trim() === product.description.trim()
              ) {
                item[commodityCodeIndex].text = product?.commodityCode;
              }
            });
          } else {
            productsForReview.push({
              id: product.id,
              hsCode: product.commodityCode,
              description: product.description,
              commodityCode: null,
            });
          }
        });

        if (productsForReview.length > 0) {
          setProductsForReviewInModal(productsForReview);
        }
        if (productsForReview.length === 0) {
          if (ga.gaInitialized) {
            window?.gtag?.('event', 'Manage Commodity Codes Auto', {
              event_category: 'Manage Commodity Codes',
            });
          }
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const manageCommodityCodesAction = () => {
    populateListOfInvalidProducts(productInformationItems);
    setShowManageCommodityCodesModal(true);
  };

  useEffect(() => {
    if (visible) {
      populateListOfInvalidProducts(productInformationItems);
    }
  }, [visible]);

  const haveProducts = spreadsheetHandle?.current?.excelState?.rows?.length > 0;
  const isLoadCreatedFromFileUpload = store.newLoadFromFile[0];
  const isFileUploadDraft = store.loadTypeDescription[0] === 'FileUpload-Draft';
  const isCM8Draft = store.loadTypeDescription[0] === 'CM8-Draft';
  const showManageCommodityCodesBtn = haveProducts && (isLoadCreatedFromFileUpload || isFileUploadDraft || isCM8Draft);

  // fetches item description from Taric microservice for each item line
  const addItemDescriptions = async () => {
    try {
      setIsLoadingDescriptions(true);

      const updatedRows = [...spreadsheetHandle.current.excelState.rows];
      const commCodeIndex = getColumnIndex('commoditycode');
      const descriptionIndex = getColumnIndex('description');
      const payload = updatedRows.map((item, idx) => ({
        itemId: idx,
        commcode: item[commCodeIndex].text,
      }));

      let hasMissingDescriptions = false;
      const descriptions = await addDescriptionMutation.mutateAsync(payload);

      if (descriptions.length) {
        descriptions.forEach((item) => {
          if (updatedRows[item.itemId]?.[descriptionIndex]?.text?.trim() === '') {
            if (item.headingDescription) {
              // if user has not already entered a description, we populate it with the description from the Taric microservice.
              updatedRows[item.itemId][descriptionIndex].text = item.headingDescription;
            } else {
              // if there is no description from the microservice, we set an error on the commodity code cell
              updatedRows[item.itemId][commCodeIndex].error = 'Invalid commodity code';
            }
            hasMissingDescriptions = true;
          }
        });

        if (hasMissingDescriptions) {
          spreadsheetHandle.current.excelDispatch({
            type: 'set',
            detail: updatedRows,
          });
        } else {
          setShowMissingDescriptionModal(true);
        }
      }
    } catch (e) {
      console.error('Error fetching commodity descriptions:', e);
    } finally {
      setIsLoadingDescriptions(false);
    }
  };

  const openMultipleConsignorsModal = () => {
    const [collapsedRows, collapsedFiles] = getCollapsedRows(spreadsheetHandle);

    // remove any empty rows first and then update the spreadsheet
    spreadsheetHandle.current.excelDispatch({
      type: 'set',
      detail: collapsedRows,
    });
    spreadsheetHandle.current.excelDispatch({
      type: 'set-files',
      detail: collapsedFiles,
    });

    // if any products have no invoice numbers, show an error
    if (hasRowsWithoutInvoiceNumbers(collapsedRows, invoiceNumberIndex)) {
      setAlertDetails(errorMessages.missingInvoiceNumbers);
      setShowAlert(true);
      return;
    }

    setIsMultipleConsignorsModalOpen(true);
  };

  const openExciseModal = () => {
    const [collapsedRows, collapsedFiles] = getCollapsedRows(spreadsheetHandle);

    // remove any empty rows first and then update the spreadsheet
    spreadsheetHandle.current.excelDispatch({
      type: 'set',
      detail: collapsedRows,
    });
    spreadsheetHandle.current.excelDispatch({
      type: 'set-files',
      detail: collapsedFiles,
    });

    setIsExciseModalOpen(true);
  };

  const onExciseModalSave = (data) => {
    const updatedRows = [...spreadsheetHandle.current.excelState.rows];
    const exciseIndex = getColumnIndex('exciseTaxCode');
    const productTypeIndex = getColumnIndex('exciseProductType');

    data.forEach((item, idx) => {
      const itemIndex = item.lineNumber - 1;
      if (updatedRows[itemIndex]) {
        updatedRows[itemIndex][exciseIndex].text = item.exciseTaxCode || '';
        updatedRows[itemIndex][productTypeIndex].text = item.exciseProductType || '';
      }
    });

    spreadsheetHandle.current.excelDispatch({
      type: 'set',
      detail: updatedRows,
    });
  };

  const checkVatAuth = async (filteredList) => {
    const vatRestrictionsPayload = {
      items: filteredList,
      consigneeId: store.consignee[0],
      directionId: store.directionSelected[0],
      epoId: null,
    };

    const vatRestrictionsData = await queryClient.fetchQuery({
      queryKey: ['vatRestrictions', vatRestrictionsPayload],
      queryFn: () => postVATRestrictions(vatRestrictionsPayload),
      staleTime: 5 * 60 * 1000,
    });

    return vatRestrictionsData[0].passwordRequired && !vatAuthEntered;
  };

  const validateItems = async (filteredList) => {
    const loadData = {
      filteredList,
      consignee: store.consignee[0],
      GBimport: store.GBimport[0],
      multipleConsignor: isMultipleConsignors,
      multipleInvoice: isMultipleInvoices,
    };

    return queryClient.fetchQuery({
      queryKey: ['validateItems', loadData],
      queryFn: () => validateLoad(loadData),
      staleTime: 5 * 60 * 1000,
    });
  };

  const hasNewWarnings = (data) => {
    if (!data.warnings?.length && !data.autoCorrections?.length) return false;
    const currentWarnings = JSON.stringify({ warnings: data.warnings, autoCorrections: data.autoCorrections });
    if (currentWarnings === previousWarnings) return false;
    setPreviousWarnings(currentWarnings);
    return true;
  };

  const onOrderFormClick = async () => {
    if (isLoadSubmitted) {
      gotoNextPage();
      return;
    }

    const filteredList = prepareProductList();
    if (!filteredList?.length) {
      return;
    }

    if (await checkVatAuth(filteredList)) {
      setShowAuthModal(true);
      return;
    }

    const data = await validateItems(filteredList);
    handleErrorsAndWarnings(data);

    if (data.errors?.length || hasErrors) {
      setAlertDetails(hasErrors ? errorMessages.missingData : errorMessages.checkProducts);
      setShowAlert(true);
      setShowConfirmErrorsModal(true);
      return;
    }

    if (hasNewWarnings(data)) return;

    store.validatedRowsReadyToSubmit[1](filteredList);
    gotoNextPage();
  };

  const handleOrderFormClick = useMutation({
    mutationFn: onOrderFormClick,
  });

  return (
    <div onClick={handleTableClicked}>
      <Toast className="error-toast">
        <Alert style={{ maxWidth: '35rem', backgroundColor: '#E4E3ED' }} show={showAlert}>
          <button className="btn-close position-absolute top-0 end-0 p-3" onClick={() => setShowAlert(false)} />
          <div className="floating-box-header-newload align-items-center" style={{ margin: 0, padding: 0 }}>
            <span>{alertDetails?.title}</span>
          </div>
          <p style={{ fontSize: '100%', fontWeight: 500 }} className="text-danger">
            {alertDetails?.description}
          </p>
        </Alert>
      </Toast>

      <ManageCommodityCodesModal
        showModal={showManageCommodityCodesModal}
        productList={productsForReviewInModal}
        spredsheetProductRows={productInformationItems}
        hsCodeMaxAllowedLength={hsCodeMaxAllowedLength}
        customerID={parseInt(store.clientID[0])}
        customerName={store.clientIDname[0]}
        customerEORINumber={store.clientIDEori[0]}
        onHide={() => setShowManageCommodityCodesModal(false)}
        setProductsForReviewInModal={() => setProductsForReviewInModal([])}
        getColumnIndex={getColumnIndex}
      />

      <ConfirmationModal
        header="Confirm Reset"
        show={showConfirmation}
        onHide={() => setShowConfirmation(false)}
        onConfirm={resetPage}
      >
        Are you sure you want to choose another input method? You will lose any products which you have already entered.
      </ConfirmationModal>

      <ConfirmationModal
        header="Add Description"
        show={showMissingDescriptionModal}
        onHide={() => setShowMissingDescriptionModal(false)}
        hideConfirm
      >
        No missing descriptions
      </ConfirmationModal>

      <PreferentialModal
        onConfirm={() => {
          nextPage();
        }}
        onHide={() => {
          setShowPreferentialConfirmation(false);
        }}
        show={showPreferentialConfirmation}
      />

      <MultipleConsignorsModal
        show={isMultipleConsignorsModalOpen}
        invoiceLines={invoiceLines}
        consignorDetails={consignorDetails}
        onHide={() => setIsMultipleConsignorsModalOpen(false)}
        onSave={handleUpdateConsignors}
        locked={isLoadSubmitted}
      />

      <ExciseModal
        items={spreadsheetHandle?.current?.excelState?.rows}
        show={isExciseModalOpen}
        onHide={() => setIsExciseModalOpen(false)}
        onSave={onExciseModalSave}
        commCodeIndex={getColumnIndex('commoditycode')}
        productTypeIndex={getColumnIndex('exciseProductType')}
        exciseCodeIndex={getColumnIndex('exciseTaxCode')}
        exciseOptions={exciseOptions}
        disabled={isLoadSubmitted}
      />

      <AuthModal
        show={showAuthModal}
        onHide={() => setShowAuthModal(false)}
        onSuccess={() => {
          setShowAuthModal(false);
          setVatAuthEntered(true);
        }}
      />

      {showBulkModal && (
        <BulkOperations
          id="cabieLoads"
          key="cabieLoads"
          onBulkClose={() => setShowBulkModal(false)}
          itemCount={itemRows}
          invoiceNumber={store.invoicenumber[0]}
          excelState={spreadsheetHandle?.current.excelState}
          totalValue={totalValue}
        />
      )}
      <div>
        <Container fluid style={{ borderRadius: '20px', backgroundColor: '#f0f4f9' }}>
          <div
            className="floating-box-header-newload flex-row"
            style={{ padding: '18px 0 0 0', marginLeft: '0', height: 'auto' }}
          >
            <span>
              Product Information{' '}
              {!!store.clientID[0] && !validateRole({ isCustomer: true }) && (
                <span style={{ fontSize: '1.0rem', marginTop: '0.3rem' }}>
                  ({store.clientIDname[0]}
                  {referenceNoDisplay})
                </span>
              )}
            </span>
            <div style={{ flexGrow: 1 }}></div>
          </div>

          <GridTotalsPI
            grossKG={grossMass}
            netKG={netMass}
            value={totalValue}
            itemCount={itemsCount}
            rows={itemRows}
            itemHeaders={itemHeaders}
            billableType={store.billableType[0]}
            readOnly={isLoadSubmitted}
          />

          <ConfirmErrorsModal
            show={showConfirmErrorsModal}
            onHide={() => setShowConfirmErrorsModal(false)}
            onConfirm={() => {
              if (showConfirmErrorsModal) {
                gotoNextPage();
                setShowConfirmErrorsModal(false);
              }
            }}
          />

          <div className="load-product-information">
            <div className="row-number-wrapper">
              <RowNumberColumn rowCount={itemRows} />
              <Spreadsheet
                handle={spreadsheetHandle}
                visible={visible}
                editable={!isLoadSubmitted}
                typeOrPaste={() => {
                  if (visible) {
                    setNewLoadDataModified(true);
                    setNewLoadItemsModified(true);
                    setPreviousWarnings('');
                  }
                }}
                resetOnConfigUpdate={false}
              >
                {isMultipleInvoices && (
                  <Spreadsheet.Column key="invoiceNumber" type="text" minWidth={11} align="center">
                    Invoice
                  </Spreadsheet.Column>
                )}
                {!store.tadEns[0] && (
                  <Spreadsheet.Column
                    key="commoditycode"
                    type="number"
                    minWidth={12}
                    align="center"
                    maxLength={10}
                    tooltip={<CommodityCodeInfoTooltip />}
                  >
                    HS Code
                  </Spreadsheet.Column>
                )}
                <Spreadsheet.Column key="origin" type="letters" minWidth={7} align="center">
                  Origin
                </Spreadsheet.Column>
                <Spreadsheet.Column key="description" type="text" stretch minWidth={17}>
                  Description
                </Spreadsheet.Column>
                <Spreadsheet.Column key="packageCount" type="number" minWidth={10}>
                  No. Items
                </Spreadsheet.Column>
                <Spreadsheet.Column
                  key="packageType"
                  type="letters"
                  minWidth={10}
                  align="center"
                  tooltip={<PackageTypeInfoTooltip />}
                >
                  Type
                </Spreadsheet.Column>
                <Spreadsheet.Column key="grossMass" type="decimal" minWidth={13} tooltip={<GrossMassInfoTooltip />}>
                  Gross Mass
                </Spreadsheet.Column>
                <Spreadsheet.Column key="netMass" type="decimal" minWidth={12} tooltip={<NetMassInfoTooltip />}>
                  Net Mass
                </Spreadsheet.Column>
                <Spreadsheet.Column key="itemValue" type="decimal" minWidth={12}>
                  Value
                </Spreadsheet.Column>
                <Spreadsheet.Column key="quantity2" type="decimal" minWidth={10} align="right">
                  Qty 2
                </Spreadsheet.Column>
                <Spreadsheet.Column key="healthCert" type="text" minWidth={25} transform={cleanseChedRef}>
                  CHED Ref
                </Spreadsheet.Column>
                <Spreadsheet.Column key="catchCert" type="text" minWidth={14}>
                  Catch Cert
                </Spreadsheet.Column>
                <Spreadsheet.Column key="meursing" type="number" minWidth={10}>
                  Meursing
                </Spreadsheet.Column>
                <Spreadsheet.Column key="organic" type="letters" default_text="NO" align="center" minWidth={9}>
                  Organic
                </Spreadsheet.Column>
                <Spreadsheet.Column
                  key="VAT"
                  type="letters"
                  minWidth={9}
                  align="center"
                  tooltip={
                    <PreferentialTooltip>
                      <h6>Valid Options</h6>
                      <ul>
                        <li>VATZ = Zero Rated</li>
                        <li>VATS = Standard Rated</li>
                      </ul>
                    </PreferentialTooltip>
                  }
                >
                  VAT
                </Spreadsheet.Column>
                <Spreadsheet.Column key="rgr" type="text" minWidth={9}>
                  RGR
                </Spreadsheet.Column>

                <Spreadsheet.Column
                  key="preferential"
                  type="letters"
                  align="center"
                  minWidth={8}
                  tooltip={
                    <PreferentialTooltip>
                      <h6>Valid Options</h6>
                      <ul>
                        <li>Yes</li>
                        <li>No</li>
                      </ul>
                    </PreferentialTooltip>
                  }
                >
                  Pref
                </Spreadsheet.Column>
                {isMultipleConsignors && (
                  <Spreadsheet.Column key="neeNoridconsignor" hidden type="text" minWidth={11}>
                    Consignor
                  </Spreadsheet.Column>
                )}
                <Spreadsheet.Column key="exciseProductType" hidden type="text" minWidth={12}>
                  Product Type
                </Spreadsheet.Column>
                <Spreadsheet.Column key="exciseTaxCode" hidden type="text" minWidth={12}>
                  Excise Code
                </Spreadsheet.Column>
                <Spreadsheet.Column key="catchCert2" hidden type="text" minWidth={12}>
                  Catch Cert
                </Spreadsheet.Column>
              </Spreadsheet>
            </div>

            <div class="d-flex py-3 px-3 w-100 justify-content-between">
              <div class="d-flex gap-3">
                {!isLoadSubmitted && showBulkBtn && (
                  <button
                    className="epo-continue-button"
                    type="button"
                    onClick={() => {
                      setShowBulkModal(true);
                    }}
                  >
                    Bulk Action
                  </button>
                )}
                {!isLoadSubmitted && showManageCommodityCodesBtn && (
                  <button
                    className="epo-continue-button manage-commodity-codes"
                    type="button"
                    onClick={manageCommodityCodesAction}
                  >
                    Manage Commodity Codes
                  </button>
                )}
                {showCheckIpaffsButton(store.directionSelected[0]) && (
                  <button
                    className="epo-continue-button manage-commodity-codes"
                    type="button"
                    onClick={() => setShowIpaffsModal(true)}
                  >
                    Check IPAFFS
                  </button>
                )}
                {!isLoadSubmitted && (
                  <button
                    className="epo-continue-button manage-commodity-codes"
                    type="button"
                    onClick={() => {
                      addItemDescriptions();
                    }}
                    disabled={isLoadingDescriptions || productInformationItems?.length === 0}
                  >
                    Add Description
                  </button>
                )}

                {isMultipleConsignors && (
                  <button
                    className={`epo-continue-button manage-commodity-codes ${isMultipleConsignorsValid && isMultipleInvoicesValid ? '' : 'error-button'}`}
                    type="button"
                    onClick={openMultipleConsignorsModal}
                    disabled={productInformationItems?.length === 0}
                  >
                    {isLoadSubmitted ? 'View' : 'Update'} Consignors
                  </button>
                )}

                <button
                  className="epo-continue-button manage-commodity-codes"
                  type="button"
                  onClick={openExciseModal}
                  disabled={isLoadingDescriptions || productInformationItems?.length === 0}
                >
                  Excise
                </button>
              </div>
              <button
                className="blue-button-newload"
                type="button"
                onClick={handleOrderFormClick.mutate}
                disabled={handleOrderFormClick.isPending}
              >
                Order Form
              </button>
            </div>
          </div>
        </Container>
      </div>
    </div>
  );
};

export default ProductInfo;
